import React, { useEffect, useState } from "react";
import CouponManager from "../../services/dashboard/CouponManager";
import UserManager from "../../services/dashboard/UserManager";
import {
  TrashIcon,
  CheckBadgeIcon,
} from "@heroicons/react/24/outline";
import { Edit2, Trash2, Calendar, Percent, Tag, DollarSign } from "lucide-react";
import toast from "react-hot-toast";

const initialFormState = {
  couponCode: "",
  discount: 0,
  minOrderValue: 0,
  maxUsageLimit: 1,
  status: "ACTIVE",
  couponType: "NEW_SUBSCRIPTION",
  usagePerUser: 1,
  isStackable: false,
  startDate: "",
  expiryDate: "",
  userId: "",
};

const AdminCouponManager = () => {
  const [coupons, setCoupons] = useState([]);
  const [formData, setFormData] = useState(initialFormState);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);

  const fetchCoupons = async () => {
    try {
      const res = await CouponManager.getAllCoupons();
      setCoupons(res.responseData || []);
    } catch (err) {
      toast.error("Failed to load coupons");
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      if (userSearchQuery.trim()) {
        try {
          const results = await UserManager.searchUsers(userSearchQuery.trim());
          setUserSearchResults(results);
        } catch (err) {
          toast.error("User search failed");
        }
      } else {
        setUserSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [userSearchQuery]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        discount: parseFloat(formData.discount),
        minOrderValue: parseFloat(formData.minOrderValue),
        maxUsageLimit: parseInt(formData.maxUsageLimit),
        usagePerUser: parseInt(formData.usagePerUser),
        startDate: new Date(formData.startDate),
        expiryDate: new Date(formData.expiryDate),
      };

      if (isEditing) {
        await CouponManager.updateCoupon(editingId, payload);
        toast.success("Coupon updated!");
      } else if (formData.userId) {
        await CouponManager.createCouponForUser(formData.userId, payload);
        toast.success("User-specific coupon created!");
      } else {
        await CouponManager.createCoupon(payload);
        toast.success("Coupon created!");
      }

      setFormData(initialFormState);
      setIsEditing(false);
      setEditingId(null);
      setUserSearchQuery("");
      fetchCoupons();
    } catch (err) {
      toast.error("Failed to save coupon");
    }
  };

  const parseDateArray = (arr) => {
    if (!Array.isArray(arr)) return null;
    return new Date(
      arr[0],
      arr[1] - 1,
      arr[2] || 1,
      arr[3] || 0,
      arr[4] || 0,
      arr[5] || 0,
      arr[6] || 0
    );
  };

  const handleEdit = (coupon) => {
    const start = parseDateArray(coupon.startDate);
    const expiry = parseDateArray(coupon.expiryDate);

    setIsEditing(true);
    setEditingId(coupon.couponId);
    setFormData({
      ...coupon,
      startDate: start && !isNaN(start) ? start.toISOString().slice(0, 16) : "",
      expiryDate: expiry && !isNaN(expiry) ? expiry.toISOString().slice(0, 16) : "",
      userId: coupon.userId || "",
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this coupon?")) {
      await CouponManager.deleteCoupon(id);
      toast.success("Coupon deleted");
      fetchCoupons();
    }
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setIsEditing(false);
    setEditingId(null);
    setUserSearchQuery("");
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-950 min-h-screen">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Coupon Management
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Create and manage discount coupons for subscriptions
            </p>
          </div>
          {isEditing && (
            <button
              onClick={resetForm}
              className="px-5 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 font-medium"
            >
              Cancel Edit
            </button>
          )}
        </header>

        {/* Form - Structured Sections */}
        <form
          onSubmit={handleSubmit}
          className="bg-white dark:bg-gray-900/60 rounded-xl shadow-sm border border-gray-200/80 dark:border-gray-800/80 overflow-hidden"
        >
          <div className="p-6 space-y-6">
            {/* Section 1: Coupon Basics */}
            <div className="space-y-3">
              <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-[0.15em]">
                Coupon Basics
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Coupon Code
                  </label>
                  <input
                    type="text"
                    name="couponCode"
                    value={formData.couponCode}
                    onChange={handleChange}
                    placeholder="e.g., SUMMER2026"
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 uppercase"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Discount Percentage
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="discount"
                      value={formData.discount}
                      onChange={handleChange}
                      placeholder="0"
                      min="0"
                      max="100"
                      className="w-full px-4 py-2 pr-11 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                      required
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400 dark:text-gray-500 pointer-events-none">
                      %
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200/80 dark:border-gray-800/80" />

            {/* Section 2: Usage Rules */}
            <div className="space-y-3">
              <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-[0.15em]">
                Usage Rules
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Minimum Order Value
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400 dark:text-gray-500 pointer-events-none">
                      ₹
                    </div>
                    <input
                      type="number"
                      name="minOrderValue"
                      value={formData.minOrderValue}
                      onChange={handleChange}
                      placeholder="0"
                      min="0"
                      className="w-full px-4 py-2 pl-9 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Max Usage Limit
                  </label>
                  <input
                    type="number"
                    name="maxUsageLimit"
                    value={formData.maxUsageLimit}
                    onChange={handleChange}
                    placeholder="1"
                    min="1"
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Usage Per User
                  </label>
                  <input
                    type="number"
                    name="usagePerUser"
                    value={formData.usagePerUser}
                    onChange={handleChange}
                    placeholder="1"
                    min="1"
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Coupon Type
                  </label>
                  <select
                    name="couponType"
                    value={formData.couponType}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100"
                    required
                  >
                    <option value="NEW_SUBSCRIPTION">New Subscription</option>
                    <option value="RENEW_SUBSCRIPTION">Renew Subscription</option>
                    <option value="CHANGE_SUBSCRIPTION">Change Subscription</option>
                    <option value="GENERIC">Generic</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center pt-1">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      name="isStackable"
                      checked={formData.isStackable}
                      onChange={handleChange}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:bg-blue-500 dark:peer-checked:bg-blue-600 transition-all duration-300 shadow-inner"></div>
                    <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-all duration-300 peer-checked:translate-x-6 shadow-md"></div>
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Stackable Coupon
                  </span>
                </label>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200/80 dark:border-gray-800/80" />

            {/* Section 3: Validity */}
            <div className="space-y-3">
              <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-[0.15em]">
                Validity Period
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Start Date & Time
                  </label>
                  <div className="relative">
                    <input
                      type="datetime-local"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      className="w-full px-4 py-2 pl-10 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100"
                      required
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none">
                      <Calendar size={16} />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Expiry Date & Time
                  </label>
                  <div className="relative">
                    <input
                      type="datetime-local"
                      name="expiryDate"
                      value={formData.expiryDate}
                      onChange={handleChange}
                      className="w-full px-4 py-2 pl-10 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100"
                      required
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none">
                      <Calendar size={16} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200/80 dark:border-gray-800/80" />

            {/* Section 4: Assignment */}
            <div className="space-y-3">
              <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-[0.15em]">
                User Assignment (Optional)
              </h3>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search user by name or email..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                />
                {userSearchResults.length > 0 && (
                  <ul className="absolute z-10 mt-2 w-full border border-gray-200/80 dark:border-gray-700/80 rounded-lg bg-white dark:bg-gray-800 shadow-lg overflow-hidden max-h-60 overflow-y-auto">
                    {userSearchResults.map((user) => (
                      <li
                        key={user.userId}
                        className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                        onClick={() => {
                          setFormData({ ...formData, userId: user.userId });
                          setUserSearchQuery(
                            `${user.firstName} ${user.lastName} (${user.emailId})`
                          );
                          setUserSearchResults([]);
                        }}
                      >
                        <div className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {user.emailId}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Footer with Submit Button */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/40 border-t border-gray-200/80 dark:border-gray-800/80 flex justify-end gap-3">
            {isEditing && (
              <button
                type="button"
                onClick={resetForm}
                className="px-5 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 font-medium"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="px-9 py-3 bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 text-white rounded-lg transition-all duration-200 font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 text-[15px]"
            >
              {isEditing ? "Update Coupon" : "Create Coupon"}
            </button>
          </div>
        </form>

        {/* Active Coupons - Card List */}
        <div>
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
            Active Coupons ({coupons.length})
          </h2>

          {coupons.length === 0 ? (
            <div className="bg-white dark:bg-gray-900/60 rounded-xl border border-gray-200/80 dark:border-gray-800/80 p-16 text-center">
              <div className="flex flex-col items-center gap-3">
                <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full">
                  <Tag className="w-8 h-8 text-gray-400 dark:text-gray-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                    No coupons yet
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Create your first coupon to get started
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {coupons.map((coupon) => (
                <div
                  key={coupon.couponId}
                  className="group bg-white dark:bg-gray-900/60 rounded-xl border border-gray-200/80 dark:border-gray-800/80 hover:border-gray-300/80 dark:hover:border-gray-700/80 hover:shadow-lg transition-all duration-200 p-5"
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Coupon Details */}
                    <div className="flex-1 min-w-0 space-y-3">
                      <div className="flex items-start gap-3">
                        <h3 className="text-[15px] font-bold text-gray-950 dark:text-gray-50 leading-tight uppercase tracking-wide">
                          {coupon.couponCode}
                        </h3>
                        <span className="inline-flex items-center px-3 py-1 rounded-lg bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 text-sm font-bold border border-orange-200/80 dark:border-orange-800/80 shadow-sm">
                          {coupon.discount}% OFF
                        </span>
                      </div>

                      {/* Tags Row */}
                      <div className="flex flex-wrap items-center gap-2">
                        {/* Status Badge */}
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-semibold border shadow-sm ${
                            coupon.status === "ACTIVE"
                              ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200/80 dark:border-green-800/80"
                              : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200/80 dark:border-gray-700/80"
                          }`}
                        >
                          {coupon.status}
                        </span>

                        {/* Type Badge */}
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-[10px] font-semibold border border-blue-200/80 dark:border-blue-800/80 shadow-sm">
                          {coupon.couponType === "NEW_SUBSCRIPTION"
                            ? "New Sub"
                            : coupon.couponType === "RENEW_SUBSCRIPTION"
                            ? "Renew"
                            : coupon.couponType === "CHANGE_SUBSCRIPTION"
                            ? "Change Plan"
                            : "Generic"}
                        </span>

                        {/* Min Order Badge */}
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-[10px] font-medium border border-gray-200/80 dark:border-gray-700/80">
                          Min: ₹{coupon.minOrderValue}
                        </span>

                        {/* Usage Badge */}
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-[10px] font-medium border border-gray-200/80 dark:border-gray-700/80">
                          Max: {coupon.maxUsageLimit} uses
                        </span>

                        {/* Stackable Badge */}
                        {coupon.isStackable && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 text-[10px] font-semibold border border-purple-200/80 dark:border-purple-800/80 shadow-sm">
                            <CheckBadgeIcon className="w-3 h-3" />
                            Stackable
                          </span>
                        )}

                        {/* User Specific Badge */}
                        {coupon.userId && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 text-[10px] font-semibold border border-indigo-200/80 dark:border-indigo-800/80 shadow-sm">
                            User Specific
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={() => handleEdit(coupon)}
                        className="p-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-400 hover:text-blue-600 dark:text-gray-500 dark:hover:text-blue-400 transition-all duration-150 hover:scale-110"
                        title="Edit coupon"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(coupon.couponId)}
                        className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400 transition-all duration-150 hover:scale-110"
                        title="Delete coupon"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCouponManager;
